import { useCallback, useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShareNodes,
  faUpRightFromSquare,
  faUpRightAndDownLeftFromCenter,
  faThumbtack,
  faThumbtackSlash,
  faCalendarDay,
} from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { convertWidgetToReactPath, useWidgetHost } from '@/core/widgets/export';
import { openPopup } from '@/shared/util/export';
import {
  getItemTypeAndIdFromUri,
  getSearchFilterTypeUrl,
} from '@/core/search/util/get-search-filter-url-by-type';
import { SearchResultItem } from '@/core/search/model/search';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { authService, useSessionId } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsActionType,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import { ClipboardButton } from '@/shared/components/export';
import { SearchContext } from '@/core/search/contexts/search-context';
import { getSearchResultType } from '@/core/search/services/search-result-type-registry';
import { useSearchActionPermissions } from '@/core/search/hooks/use-search-action-permissions';

export default function SearchActions({
  item,
  triggerDefaultAction,
  setTriggerDefaultAction,
}: {
  item: SearchResultItem;
  triggerDefaultAction: string | null;
  setTriggerDefaultAction: (action: string | null) => void;
}) {
  const subject = authService.getSubjectData();
  const sessionId = useSessionId();

  const widgetHost = useWidgetHost();
  const openWidget = useOpenWidget();
  const { setHideSearchResults, setPreviewed } = useContext(SearchContext)!;
  const { canPerformAction } = useSearchActionPermissions();
  const { itemType, itemId } = getItemTypeAndIdFromUri(item?.uri);
  const provider = getSearchResultType(itemType);
  const itemPathId =
    itemType === 'article' ? item.filters?.find(f => f.key === 'externalId')?.value || '' : itemId;
  const [pinned, setPinned] = useState(
    widgetHost.activeWidgetIds.includes('/pinned-series/' + itemPathId),
  );

  const recordAnalyticsEvent = useCallback(
    async (actionType: AnalyticsActionType) => {
      const analyticsEventObject = async (actionType: AnalyticsActionType) => {
        if (provider?.analyticsObject) return provider.analyticsObject(item, actionType);
        switch (itemType) {
          case 'genre':
            const { itemType: genreSchema, itemId: genreCode } =
              getItemTypeAndIdFromUri(itemPathId);
            return {
              type: AnalyticsTypes.ARTICLE_SERIES,
              actionType,
              id: itemId,
              code: genreCode,
              scheme: genreSchema,
              display: item.title,
              description: item.short_description,
              longDescription: item.long_description,
            };
        }
      };

      analyticsService.recordAnalyticsEvent(AnalyticsEvent.SEARCH_ACTION, {
        subject,
        object: await analyticsEventObject(actionType),
        session: {
          id: sessionId,
        },
      });
    },
    [item, itemId, itemPathId, itemType, provider, subject, sessionId],
  );

  const openPreview = useCallback(async () => {
    if (!itemType || !provider?.preview) return;
    const id = await provider.preview(item);
    recordAnalyticsEvent(AnalyticsActionType.EXPAND);
    setPreviewed({ type: itemType, id });
  }, [item, itemType, provider, setPreviewed, recordAnalyticsEvent]);

  const canOpenItem = !!provider?.open && (provider.canOpen?.(item) ?? true);

  const openItem = useCallback(() => {
    if (!provider?.open || !canOpenItem) return;
    provider.open(item, { subject, sessionId });
    recordAnalyticsEvent(AnalyticsActionType.EXPAND);
  }, [item, provider, canOpenItem, subject, sessionId, recordAnalyticsEvent]);

  const togglePinGenre = useCallback(() => {
    if (pinned) {
      widgetHost.removeWidgetById(`/pinned-series/${itemPathId}`);
      recordAnalyticsEvent(AnalyticsActionType.UNPIN);
    } else {
      openWidget(`/pinned-series/${itemPathId}`);
      recordAnalyticsEvent(AnalyticsActionType.PIN);
      setHideSearchResults(true);
    }
    setPinned(!pinned);
  }, [pinned, itemPathId, widgetHost, openWidget, setHideSearchResults, recordAnalyticsEvent]);

  const addHSCWidget = useCallback(() => {
    recordAnalyticsEvent(AnalyticsActionType.WIDGET_NAVIGATE);
    setHideSearchResults(true);
    openWidget('/calendar', `/event/${itemPathId}`);
  }, [recordAnalyticsEvent, setHideSearchResults, itemPathId, openWidget]);

  useEffect(() => {
    if (triggerDefaultAction === item.uri) {
      if (provider?.preview) openPreview();
      else if (canOpenItem) openItem();
      else
        switch (itemType) {
          case 'event':
            addHSCWidget();
            break;
          case 'genre':
            togglePinGenre();
            break;
        }
      setTriggerDefaultAction(null);
    }
  }, [
    item,
    itemType,
    triggerDefaultAction,
    addHSCWidget,
    setTriggerDefaultAction,
    provider,
    canOpenItem,
    openPreview,
    openItem,
    togglePinGenre,
  ]);

  return (
    <div className="d-flex justify-content-end">
      {provider?.ItemActions && (
        <provider.ItemActions item={item} recordAction={recordAnalyticsEvent} />
      )}
      {itemType === 'genre' &&
        item.filters?.find(f => f.key === 'pinnable')?.value === 'true' &&
        canPerformAction('pin') && (
          <button className={'btn btn-link btn-sm p-1 py-0'} onClick={togglePinGenre}>
            {pinned ? (
              <OverlayTrigger
                placement="right"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip id="unpin-tooltip">Unpin</Tooltip>}
              >
                <FontAwesomeIcon icon={faThumbtackSlash} />
              </OverlayTrigger>
            ) : (
              <OverlayTrigger
                placement="right"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip id="pin-tooltip">Pin</Tooltip>}
              >
                <FontAwesomeIcon icon={faThumbtack} />
              </OverlayTrigger>
            )}
          </button>
        )}
      {itemType && itemType !== 'pdf' && canPerformAction('share') && (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="share-tooltip">Share</Tooltip>}
        >
          <button
            className={'btn btn-link btn-sm p-1 py-0'}
            onClick={() => recordAnalyticsEvent(AnalyticsActionType.COPY_PATH)}
          >
            <ClipboardButton
              text={`${window.location.origin}${convertWidgetToReactPath(getSearchFilterTypeUrl(itemType), itemPathId)}`}
            >
              <FontAwesomeIcon icon={faShareNodes} />
            </ClipboardButton>
          </button>
        </OverlayTrigger>
      )}
      {(provider?.preview || canOpenItem) && canPerformAction('expand') ? (
        <OverlayTrigger
          placement="left"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="share-tooltip">Expand</Tooltip>}
        >
          {provider.preview ? (
            <div onClick={() => recordAnalyticsEvent(AnalyticsActionType.EXPAND)}>
              <button className={'btn btn-link btn-sm p-1 py-0'} onClick={() => openPreview()}>
                <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
              </button>
            </div>
          ) : (
            // `openItem` records EXPAND itself
            <div>
              <button className={'btn btn-link btn-sm p-1 py-0'} onClick={openItem}>
                <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
              </button>
            </div>
          )}
        </OverlayTrigger>
      ) : itemType === 'event' ? (
        <OverlayTrigger
          placement="left"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="calendar-tooltip">See in calendar</Tooltip>}
        >
          <button className={'btn btn-link btn-sm p-1 py-0'} onClick={addHSCWidget}>
            <FontAwesomeIcon icon={faCalendarDay} />
          </button>
        </OverlayTrigger>
      ) : itemType !== 'pdf' && canPerformAction('open') ? (
        <OverlayTrigger
          placement="left"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="share-tooltip">Open in new tab</Tooltip>}
        >
          <div onClick={() => recordAnalyticsEvent(AnalyticsActionType.OPEN_TARGET_BLANK)}>
            <button
              className={'btn btn-link btn-sm p-1 py-0'}
              onClick={() =>
                itemType
                  ? openPopup(
                      `${convertWidgetToReactPath(getSearchFilterTypeUrl(itemType), itemPathId)}`,
                    )
                  : undefined
              }
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} />
            </button>
          </div>
        </OverlayTrigger>
      ) : null}
    </div>
  );
}
